import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, query, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { Trash2, Eye, Calendar } from 'lucide-react';
import { calculateFinalPrice } from './costSheetUtils';

const CostSheetList = ({ onView, onBack, currentUser }) => {
  const [costSheets, setCostSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    let isMounted = true;
    
    const fetchCostSheets = async () => {
      try {
        setLoading(true);
        const q = query(collection(db, 'costSheets'));
        const snapshot = await getDocs(q);
        
        if (isMounted) {
          const sheets = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          // Sort by createdAt descending (newest first)
          sheets.sort((a, b) => {
            const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 
                         a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0;
            const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 
                         b.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0;
            return bTime - aTime; // Descending order
          });
          setCostSheets(sheets);
          setLoading(false);
        }
      } catch (error) {
        console.error('Firestore error fetching cost sheets:', error);
        if (isMounted) {
          setCostSheets([]);
          setLoading(false);
        }
      }
    };

    fetchCostSheets();
    
    // Refresh every 30 seconds to get updates
    const interval = setInterval(fetchCostSheets, 30000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this cost sheet?')) {
      try {
        await deleteDoc(doc(db, 'costSheets', id));
      } catch (error) {
        console.error('Error deleting cost sheet:', error);
        alert('Failed to delete cost sheet');
      }
    }
  };

  const filteredSheets = costSheets.filter(sheet => {
    const searchLower = searchQuery.toLowerCase();
    return (
      sheet.supplierName?.toLowerCase().includes(searchLower) ||
      sheet.model?.toLowerCase().includes(searchLower) ||
      sheet.description?.toLowerCase().includes(searchLower) ||
      sheet.hsCode?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-gray-500">Loading cost sheets...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 rounded-md hover:bg-gray-200 transition-colors"
              title="Back"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-2xl font-bold">Past Cost Sheets</h1>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <input
            type="text"
            placeholder="Search by supplier, model, description, or HS code..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
          />
        </div>

        {/* List */}
        {filteredSheets.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <p className="text-gray-500">
              {searchQuery ? 'No cost sheets found matching your search.' : 'No cost sheets yet.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {filteredSheets.map((sheet) => {
              const finalPrice = calculateFinalPrice({
                inputCost: sheet.inputCost,
                shippingCost: sheet.shippingCost,
                interestAmount: sheet.interestAmount,
                margin: sheet.margin,
                exchangeRate: sheet.exchangeRate,
                currency: sheet.currency
              });

              return (
                <div
                  key={sheet.id}
                  className="bg-white rounded-lg shadow-sm p-6 border border-gray-200 hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-3">
                        <h3 className="text-lg font-semibold">{sheet.supplierName}</h3>
                        {sheet.model && (
                          <span className="text-sm text-gray-500">Model: {sheet.model}</span>
                        )}
                        <span className="text-sm text-gray-500 capitalize">
                          Mode: {sheet.shippingMode}
                        </span>
                      </div>

                      {sheet.description && (
                        <p className="text-gray-600 mb-2">{sheet.description}</p>
                      )}

                      <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                        <span>Qty: {sheet.quantity}</span>
                        {sheet.hsCode && <span>HS Code: {sheet.hsCode}</span>}
                        <span>Currency: {sheet.currency}</span>
                        <span>Margin: {sheet.margin}%</span>
                        {sheet.createdAt?.toDate && (
                          <span className="flex items-center gap-1">
                            <Calendar size={14} />
                            {sheet.createdAt.toDate().toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="ml-6 text-right">
                      <div className="text-sm text-gray-500 mb-1">Final Price</div>
                      <div className="text-2xl font-bold mb-4">
                        {finalPrice.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })} AED
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => onView(sheet)}
                          className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm flex items-center gap-2"
                        >
                          <Eye size={16} />
                          View
                        </button>
                        {(currentUser?.role === 'master' || currentUser?.role === 'auto-master') && (
                          <button
                            onClick={() => handleDelete(sheet.id)}
                            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm flex items-center gap-2"
                          >
                            <Trash2 size={16} />
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CostSheetList;

